import { FaAnglesLeft, FaAnglesRight, FaChevronLeft, FaChevronRight, FaClock, FaPowerOff, FaSignal, FaTrashCan } from "react-icons/fa6"
import { GenieAcsSkeletonCardComponent } from "../components/SkeletonCard"
import { GenieAcsStatusBadgeComponent } from "../components/StatusBadge"
import { formatDate } from "../../../../utilities/formatDate"
import { getRxPowerColorComponent } from "../components/GetRxPowerColor"
import { useNavigate } from "react-router-dom"

export const GenieAcsMobileCardListSection = ({ loading, itemsPerPage, data, handleAction, handlePageChange, currentPage, totalPages }) => {

    const navigate = useNavigate();

    return (
        <section className="lg:hidden space-y-3">
            {loading ? (
                [...Array(4)].map((_, i) => <GenieAcsSkeletonCardComponent key={i} />)
            ) : !data || data?.length === 0 ? (
                <div className="p-8 text-center text-slate-400">No devices found.</div>
            ) : (
                data.map((item) => (
                    <article
                        key={item._id}
                        className="rounded-2xl overflow-hidden transition-all active:scale-[0.98] cursor-pointer"
                        style={{
                            background: "rgba(15, 23, 42, 0.6)",
                            border: "1px solid rgba(51, 65, 85, 0.4)",
                        }}
                        onClick={() => navigate(`/dashboard/genie-acs/detail/${item?.id_modem}`)}
                    >
                        {/* Header: Status + ID Pelanggan */}
                        <div
                            className="px-4 py-3 flex justify-between items-center"
                            style={{ background: "rgba(15, 23, 42, 0.8)", borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}
                        >
                            <div className="flex items-center gap-2">
                                <GenieAcsStatusBadgeComponent status={item.status} />
                            </div>
                            <span className="text-xs font-bold text-slate-400 font-mono">{item.customer_id}</span>
                        </div>

                        <div className="p-4">
                            {/* Tags / Label Pelanggan */}
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-sm text-slate-300 font-bold shrink-0">
                                    {item?.tags?.[0]?.charAt(0) || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap gap-1.5">
                                        {item?.tags && item?.tags.length > 0 ? (
                                            item.tags.map((tag, idx) => (
                                                <span
                                                    key={idx}
                                                    className="inline-block px-2 py-0.5 text-[11px] font-medium rounded-md bg-slate-700/60 text-slate-300 border border-slate-600/30"
                                                >
                                                    {tag}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-slate-500 italic">No label</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 mt-1 text-slate-500 text-xs">
                                        <span className="font-mono">{item.ip_address}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Info Grid: Model + Rx Power */}
                            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                                <div className="bg-slate-800/40 p-2.5 rounded-xl border border-slate-700/30">
                                    <span className="text-slate-500 block mb-1">Model</span>
                                    <span className="text-slate-300 font-medium truncate block">{item?.device_info?.product_class || "-"}</span>
                                </div>
                                <div className="bg-slate-800/40 p-2.5 rounded-xl border border-slate-700/30">
                                    <span className="text-slate-500 block mb-1">Rx Power</span>
                                    <div className="flex items-center gap-1.5">
                                        <FaSignal className={`text-[10px] ${getRxPowerColorComponent(item.rx_power)}`} />
                                        <span className={`font-bold ${getRxPowerColorComponent(item.rx_power)}`}>
                                            {item.rx_power} dBm
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Footer: Last Inform + Actions */}
                            <div
                                className="flex items-center justify-between pt-3"
                                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
                            >
                                <div className="flex items-center gap-1.5 text-slate-500 text-[11px]">
                                    <FaClock className="text-[10px]" />
                                    <span>{formatDate(item.last_inform)}</span>
                                </div>
                                <div className="flex gap-1.5">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleAction('reboot', item); }}
                                        className="p-2 rounded-xl text-slate-400 hover:text-cyan-400 bg-slate-800/40 border border-slate-700/50 transition-colors"
                                    >
                                        <FaPowerOff className="text-sm" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleAction('delete', item); }}
                                        className="p-2 rounded-xl text-slate-400 hover:text-red-400 bg-slate-800/40 border border-slate-700/50 transition-colors"
                                    >
                                        <FaTrashCan className="text-sm" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </article>
                ))
            )}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
                <div className="flex flex-col gap-3 pt-4 pb-2 px-1 border-t border-slate-700/30">
                    {/* Info Count */}
                    <p className="text-[11px] text-slate-500 text-center">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, data?.length)} of {data?.length}
                    </p>

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="p-2.5 rounded-xl text-slate-400 hover:text-cyan-400 bg-slate-800/50 border border-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <FaAnglesLeft className="text-xs" />
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed bg-slate-800/50 border border-slate-700/50 transition-colors"
                            >
                                <FaChevronLeft className="text-xs" />
                                Prev
                            </button>
                        </div>

                        <span className="text-sm font-medium text-slate-400">
                            <span className="text-cyan-400">{currentPage}</span>
                            <span className="text-slate-600 mx-1">/</span>
                            {totalPages}
                        </span>

                        <div className="flex gap-1">
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed bg-slate-800/50 border border-slate-700/50 transition-colors"
                            >
                                Next
                                <FaChevronRight className="text-xs" />
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                className="p-2.5 rounded-xl text-slate-400 hover:text-cyan-400 bg-slate-800/50 border border-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <FaAnglesRight className="text-xs" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}